import m from 'mithril'
import type { ListSummary, Profile, Review } from '../../shared/types.ts'
import { api, apiBase } from '../api.ts'
import { state } from '../state.ts'
import { Empty, ErrorNote, Spinner, formatDate, visibilityLabel } from '../ui.ts'
import { ReviewCard } from './ReviewCard.ts'

export const ProfileView: m.ClosureComponent<{ username: string }> = (vnode) => {
  const username = vnode.attrs.username
  let profile: Profile | null = null
  let reviews: Review[] = []
  let lists: ListSummary[] = []
  let error = ''
  let busy = false

  const load = async () => {
    try {
      profile = await api<Profile>(`/api/users/${username}`)
      if (profile.visible) {
        reviews = await api<Review[]>(`/api/users/${username}/reviews`)
        lists = await api<ListSummary[]>(`/api/users/${username}/lists`)
      } else {
        reviews = []
        lists = []
      }
      error = ''
    } catch (err) {
      error = (err as Error).message
    } finally {
      m.redraw()
    }
  }

  const toggleFollow = async () => {
    if (!profile) return
    busy = true
    try {
      profile = await api<Profile>(`/api/users/${username}/follow`, {
        method: profile.isFollowedByMe ? 'DELETE' : 'POST',
      })
    } catch (err) {
      error = (err as Error).message
    } finally {
      busy = false
      m.redraw()
    }
  }

  return {
    oninit: load,
    view: () => {
      if (error && !profile) return m(ErrorNote, { text: error })
      if (!profile) return m(Spinner)

      const feedUrl = `${apiBase || location.origin}/feed/u/${profile.username}.xml`

      return m('section.stack', [
        m('header.card.profile-header', [
          m('h1', profile.displayName),
          m('p.muted', `@${profile.username}, joined ${formatDate(profile.createdAt)}`),
          profile.bio ? m('p', profile.bio) : null,
          m('div.counts', [
            m('span', [m('strong', profile.reviewCount), ' reviews']),
            m('span', [m('strong', profile.followerCount), ' followers']),
            m('span', [m('strong', profile.followingCount), ' following']),
          ]),
          m('div.row', [
            state.me && !profile.isMe
              ? m(
                  'button.button',
                  {
                    class: profile.isFollowedByMe ? '' : 'primary',
                    onclick: toggleFollow,
                    disabled: busy,
                  },
                  profile.isFollowedByMe ? 'Following' : 'Follow',
                )
              : null,
            profile.isMe ? m(m.route.Link, { href: '/settings', class: 'button' }, 'Edit profile') : null,
            profile.isPrivate
              ? null
              : m('a.button.small', { href: feedUrl, target: '_blank', rel: 'noopener' }, 'RSS'),
          ]),
        ]),

        error ? m(ErrorNote, { text: error }) : null,

        profile.visible && lists.length
          ? m('section.card.stack', [
              m('h2', 'Lists'),
              m(
                'div.chips',
                lists.map((list) =>
                  m(m.route.Link, { href: `/l/${list.id}`, key: list.id, class: 'chip' }, [
                    list.name,
                    m('span.chip-note', `${list.itemCount}`),
                    list.visibility !== 'public' ? m('span.chip-note', visibilityLabel(list.visibility).toLowerCase()) : null,
                  ]),
                ),
              ),
            ])
          : null,

        profile.visible
          ? reviews.length
            ? m('div.stack', reviews.map((review) => m(ReviewCard, { key: review.id, review, hideAuthor: true })))
            : m(Empty, { text: 'No reviews yet.' })
          : m(Empty, { text: 'This account is private.' }),
      ])
    },
  }
}
